/**
 * Чтение .xlsx прямо в браузере: значения ячеек и картинки.
 * Поддерживаются фото, вставленные в ячейку (Excel 365 «Поместить в ячейку», WPS DISPIMG)
 * и плавающие поверх ячеек (привязка по верхнему левому углу) — как в версии 3.13.2.
 */
import { unzipSync, strFromU8 } from 'fflate';

export const SUPPORTED_IMAGE: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp'
};

const NS_R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

export interface SheetRef {
  name: string;
  path: string;
}

export interface SheetImage {
  row: number;
  col: number;
  media: string;
}

export interface SheetData {
  /** Значения: строка → столбец → текст (номера с 1, как в Excel). */
  cells: Map<number, Map<number, string>>;
  images: SheetImage[];
  merges: { r1: number; c1: number; r2: number; c2: number }[];
  maxRow: number;
  maxCol: number;
}

export function columnName(n: number): string {
  let s = '';
  let x = n;
  while (x > 0) {
    const m = (x - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

function columnNumber(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function parseRef(ref: string | null): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/i.exec(ref ?? '');
  return m ? { col: columnNumber(m[1]!), row: Number(m[2]) } : null;
}

function dirOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(0, i) : '';
}

function resolvePath(base: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1);
  const parts = base ? base.split('/') : [];
  for (const p of target.split('/')) {
    if (p === '.' || p === '') continue;
    if (p === '..') parts.pop();
    else parts.push(p);
  }
  return parts.join('/');
}

function extOf(path: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(path);
  return m ? m[1]!.toLowerCase() : '';
}

/** Книга Excel, открытая из файла. */
export class Workbook {
  private files: Record<string, Uint8Array>;
  private sharedStrings: string[] = [];
  private richImage: ((vm: number) => string) | null = null;
  private wpsImages: Record<string, string> = {};
  private blobUrls = new Map<string, string>();
  sheets: SheetRef[] = [];

  private constructor(files: Record<string, Uint8Array>) {
    this.files = files;
  }

  static async open(file: File): Promise<Workbook> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let files: Record<string, Uint8Array>;
    try {
      files = unzipSync(bytes);
    } catch {
      throw new Error('Не удалось открыть файл. Старый формат .xls не поддерживается — пересохраните книгу как .xlsx.');
    }
    const wb = new Workbook(files);
    await wb.init();
    return wb;
  }

  private text(path: string): string {
    const f = this.files[path];
    return f ? strFromU8(f) : '';
  }

  private xml(path: string): Document | null {
    const t = this.text(path);
    return t ? new DOMParser().parseFromString(t, 'application/xml') : null;
  }

  private els(node: Document | Element, local: string): Element[] {
    return Array.from(node.getElementsByTagNameNS('*', local));
  }

  private rels(path: string): Record<string, { type: string; target: string }> {
    const dir = dirOf(path);
    const relPath = (dir ? dir + '/' : '') + '_rels/' + path.split('/').pop() + '.rels';
    const doc = this.xml(relPath);
    const out: Record<string, { type: string; target: string }> = {};
    if (!doc) return out;
    for (const r of this.els(doc, 'Relationship')) {
      const id = r.getAttribute('Id') ?? '';
      out[id] = { type: r.getAttribute('Type') ?? '', target: resolvePath(dir, r.getAttribute('Target') ?? '') };
    }
    return out;
  }

  private relAttr(node: Element, name: string): string {
    return node.getAttributeNS(NS_R, name) || node.getAttribute('r:' + name) || '';
  }

  private async init(): Promise<void> {
    const wbDoc = this.xml('xl/workbook.xml');
    if (!wbDoc) throw new Error('Это не файл .xlsx (нет xl/workbook.xml). Пересохраните книгу как .xlsx.');
    const wbRels = this.rels('xl/workbook.xml');

    const ssRel = Object.values(wbRels).find((r) => /\/sharedStrings$/.test(r.type));
    const ssDoc = this.xml(ssRel?.target ?? 'xl/sharedStrings.xml');
    if (ssDoc) {
      this.sharedStrings = this.els(ssDoc, 'si').map((si) =>
        this.els(si, 't')
          .filter((t) => (t.parentNode as Element)?.localName !== 'rPh')
          .map((t) => t.textContent ?? '')
          .join('')
      );
    }
    this.richImage = this.loadRichImages();
    this.wpsImages = this.loadWpsImages();

    this.sheets = this.els(wbDoc, 'sheet')
      .map((s) => ({ name: s.getAttribute('name') ?? '', path: wbRels[this.relAttr(s, 'id')]?.target ?? '' }))
      .filter((s) => !!s.path);
    if (!this.sheets.length) throw new Error('В книге не найдено листов.');
  }

  /** Excel 365: фото «в ячейке» (rich value). */
  private loadRichImages(): ((vm: number) => string) | null {
    const md = this.xml('xl/metadata.xml');
    const rv = this.xml('xl/richData/rdrichvalue.xml');
    const relDoc = this.xml('xl/richData/richValueRel.xml');
    if (!md || !rv || !relDoc) return null;
    const types = this.els(md, 'metadataType').map((t) => t.getAttribute('name'));
    const vmNode = this.els(md, 'valueMetadata')[0];
    const vmBks = vmNode
      ? this.els(vmNode, 'bk').map((bk) => {
          const rc = this.els(bk, 'rc')[0];
          if (!rc) return -1;
          const typeName = types[Number(rc.getAttribute('t')) - 1];
          return typeName === 'XLRICHVALUE' ? Number(rc.getAttribute('v')) : -1;
        })
      : [];
    const fm = this.els(md, 'futureMetadata').find((f) => f.getAttribute('name') === 'XLRICHVALUE');
    const fmBks = fm
      ? this.els(fm, 'bk').map((bk) => {
          const rvb = this.els(bk, 'rvb')[0];
          return rvb ? Number(rvb.getAttribute('i')) : -1;
        })
      : [];
    const stDoc = this.xml('xl/richData/rdrichvaluestructure.xml');
    const structs = stDoc ? this.els(stDoc, 's').map((s) => this.els(s, 'k').map((k) => k.getAttribute('n'))) : [];
    const rels = this.rels('xl/richData/richValueRel.xml');
    const relTargets = this.els(relDoc, 'rel').map((r) => rels[this.relAttr(r, 'id')]?.target ?? '');
    const rvs = this.els(rv, 'rv').map((node) => {
      const keys = structs[Number(node.getAttribute('s') ?? 0)] ?? ['_rvRel:LocalImageIdentifier'];
      const pos = keys.indexOf('_rvRel:LocalImageIdentifier');
      if (pos < 0) return '';
      const vals = this.els(node, 'v').map((v) => v.textContent ?? '');
      const relIdx = Number(vals[pos]);
      return Number.isFinite(relIdx) ? (relTargets[relIdx] ?? '') : '';
    });
    return (vm: number) => {
      const a = vmBks[vm - 1];
      let rvIndex: number;
      if (a !== undefined) {
        if (a < 0) return '';
        rvIndex = fmBks[a] !== undefined && fmBks[a]! >= 0 ? fmBks[a]! : a;
      } else {
        rvIndex = vm - 1;
      }
      return rvs[rvIndex] ?? '';
    };
  }

  /** WPS Office: фото в ячейке через формулу DISPIMG. */
  private loadWpsImages(): Record<string, string> {
    const doc = this.xml('xl/cellimages.xml');
    const map: Record<string, string> = {};
    if (!doc) return map;
    const rels = this.rels('xl/cellimages.xml');
    for (const ci of this.els(doc, 'cellImage')) {
      const pr = this.els(ci, 'cNvPr')[0];
      const blip = this.els(ci, 'blip')[0];
      if (!pr || !blip) continue;
      const target = rels[this.relAttr(blip, 'embed')]?.target;
      const name = pr.getAttribute('name');
      if (target && name) map[name] = target;
    }
    return map;
  }

  /** Значения и картинки одного листа. */
  readSheet(sheet: SheetRef): SheetData {
    const doc = this.xml(sheet.path);
    if (!doc) throw new Error(`Не удалось прочитать лист «${sheet.name}».`);
    const cells = new Map<number, Map<number, string>>();
    const images: SheetImage[] = [];
    let maxRow = 0;
    let maxCol = 0;

    for (const rowNode of this.els(doc, 'row')) {
      const rowNo = Number(rowNode.getAttribute('r'));
      let lastCol = 0;
      for (const c of Array.from(rowNode.children).filter((n) => n.localName === 'c')) {
        const ref = parseRef(c.getAttribute('r'));
        const row = ref ? ref.row : rowNo;
        const col = ref ? ref.col : lastCol + 1;
        lastCol = col;
        const t = c.getAttribute('t') ?? '';
        const vNode = this.els(c, 'v')[0];
        const fNode = this.els(c, 'f')[0];
        let value = '';
        if (t === 's' && vNode) value = this.sharedStrings[Number(vNode.textContent)] ?? '';
        else if (t === 'inlineStr') {
          const is = this.els(c, 'is')[0];
          value = is
            ? this.els(is, 't')
                .map((x) => x.textContent ?? '')
                .join('')
            : '';
        } else if (vNode) value = vNode.textContent ?? '';

        const vm = c.getAttribute('vm');
        if (vm && this.richImage) {
          const media = this.richImage(Number(vm));
          if (media) {
            images.push({ row, col, media });
            value = '';
          }
        }
        if (fNode && /DISPIMG/i.test(fNode.textContent ?? '')) {
          const m = /DISPIMG\(\s*"([^"]+)"/i.exec(fNode.textContent ?? '');
          const media = m ? this.wpsImages[m[1]!] : undefined;
          if (media) images.push({ row, col, media });
          value = '';
        }
        if (value !== '') {
          let line = cells.get(row);
          if (!line) cells.set(row, (line = new Map()));
          line.set(col, value);
          maxRow = Math.max(maxRow, row);
          maxCol = Math.max(maxCol, col);
        }
      }
    }

    const merges = this.els(doc, 'mergeCell')
      .map((m) => {
        const [a, b] = String(m.getAttribute('ref')).split(':');
        const p1 = parseRef(a ?? '');
        const p2 = parseRef(b ?? a ?? '');
        return p1 && p2 ? { r1: p1.row, c1: p1.col, r2: p2.row, c2: p2.col } : null;
      })
      .filter((x): x is { r1: number; c1: number; r2: number; c2: number } => !!x);

    // Плавающие рисунки
    for (const rel of Object.values(this.rels(sheet.path))) {
      if (!/\/drawing$/.test(rel.type) || !rel.target) continue;
      const dDoc = this.xml(rel.target);
      if (!dDoc) continue;
      const dRels = this.rels(rel.target);
      const anchors = [...this.els(dDoc, 'twoCellAnchor'), ...this.els(dDoc, 'oneCellAnchor')];
      for (const anchor of anchors) {
        const from = this.els(anchor, 'from')[0];
        if (!from) continue;
        const rowNode = this.els(from, 'row')[0];
        if (!rowNode) continue;
        const row = Number(rowNode.textContent) + 1;
        const colNode = this.els(from, 'col')[0];
        const col = colNode ? Number(colNode.textContent) + 1 : 0;
        for (const b of this.els(anchor, 'blip')) {
          const target = dRels[this.relAttr(b, 'embed')]?.target;
          if (target) images.push({ row, col, media: target });
        }
      }
    }

    for (const img of images) {
      maxRow = Math.max(maxRow, img.row);
      maxCol = Math.max(maxCol, img.col);
    }
    return { cells, images, merges, maxRow, maxCol };
  }

  /** Значение с учётом объединённых ячеек. */
  static valueAt(data: SheetData, row: number, col: number): string {
    const v = data.cells.get(row)?.get(col);
    if (v !== undefined) return v;
    for (const m of data.merges) {
      if (row >= m.r1 && row <= m.r2 && col >= m.c1 && col <= m.c2) {
        return data.cells.get(m.r1)?.get(m.c1) ?? '';
      }
    }
    return '';
  }

  /** Картинка как файл для загрузки на сервер. */
  imageFile(media: string): File | null {
    const bytes = this.files[media];
    if (!bytes) return null;
    const ext = extOf(media);
    const type = SUPPORTED_IMAGE[ext];
    if (!type) return null;
    const name = media.split('/').pop() ?? 'image.' + ext;
    return new File([bytes.slice().buffer as ArrayBuffer], name, { type });
  }

  /** Ссылка для показа картинки в окне импорта. */
  imageUrl(media: string): string {
    const cached = this.blobUrls.get(media);
    if (cached) return cached;
    const file = this.imageFile(media);
    if (!file) return '';
    const url = URL.createObjectURL(file);
    this.blobUrls.set(media, url);
    return url;
  }

  release(): void {
    for (const url of this.blobUrls.values()) URL.revokeObjectURL(url);
    this.blobUrls.clear();
  }
}
